import dayjs from 'dayjs';
import type { HackathonPayload } from '../services/hackathon.service';

export type HackathonPhase = 'upcoming' | 'ideas-open' | 'in-progress' | 'presenting' | 'voting' | 'awaiting-results' | 'results' | 'ended';

export interface HackathonStatus {
    phase: HackathonPhase;
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';
}

/**
 * When idea submission actually closes. The deadline picker is date-only and stores midnight at the start of
 * the chosen day, so "ideas due today" means until the end of today.
 */
export const ideaDeadlineEnd = (deadline: Date | null | undefined) => deadline ? dayjs(deadline).endOf('day') : null;

/**
 * Signed in but not in this hackathon (not a participant, judge or organizer): the server only lets such people in to
 * read a past hackathon of an organization they're connected to, so everything that changes something is hidden.
 */
export const isReadOnlyViewer = (hackathon: Pick<HackathonPayload, 'participants' | 'judges' | 'organizers'>, email?: string) =>
    !!email && ![...hackathon.participants, ...hackathon.judges, ...hackathon.organizers].some(p => p.email === email);

/** Whether the winners are out. An open page learns it from the live board before its own copy of the winners catches up. */
export const resultsAnnounced = (hackathon: Pick<HackathonPayload, 'winners' | 'resultsAnnounced'>) =>
    hackathon.resultsAnnounced ?? !!hackathon.winners?.length;

/** Whether new ideas are accepted: until the deadline's day is over and the results are announced. */
export const ideasOpen = (hackathon: Pick<HackathonPayload, 'ideaSubmissionDeadline' | 'winners' | 'resultsAnnounced' | 'ideasClosedAt'>, now = new Date()) => {
    // closed early by the organizers (for participants; organizers can still add ideas)
    if (resultsAnnounced(hackathon) || hackathon.ideasClosedAt) {
        return false;
    }
    const end = ideaDeadlineEnd(hackathon.ideaSubmissionDeadline);
    return !end || end.isAfter(now);
}

type StatusFields = Pick<HackathonPayload, 'votingPeriodOn' | 'endDate' | 'startDate' | 'ideaSubmissionDeadline' | 'winners' | 'resultsAnnounced' | 'ideasClosedAt'>;

/**
 * The event's current phase. `presenting` (a room has a team on stage right now, from the live board) outranks the
 * dates, so the page doesn't still say "Accepting ideas" while teams present.
 */
export const getHackathonStatus = (hackathon: StatusFields, now = new Date(), presenting = false, presented = false): HackathonStatus => {
    if (hackathon.votingPeriodOn) {
        return { phase: 'voting', label: 'Voting live', color: 'warning' };
    }
    // announcing winners ends the event, whatever the dates say
    if (resultsAnnounced(hackathon)) {
        return { phase: 'results', label: 'Results are in', color: 'secondary' };
    }
    if (presenting) {
        return { phase: 'presenting', label: 'Presentations live', color: 'warning' };
    }
    // teams have presented and voting is closed, but no winners yet: not "Accepting ideas" just because the deadline is today (run027)
    if (presented) {
        return { phase: 'awaiting-results', label: 'Results coming', color: 'info' };
    }
    // date pickers store midnight, so an event is over at the end of its end date
    if (hackathon.endDate && dayjs(hackathon.endDate).endOf('day').isBefore(now)) {
        return { phase: 'ended', label: 'Ended', color: 'default' };
    }
    if (!hackathon.ideasClosedAt && hackathon.ideaSubmissionDeadline && ideaDeadlineEnd(hackathon.ideaSubmissionDeadline)!.isAfter(now)) {
        return { phase: 'ideas-open', label: 'Accepting ideas', color: 'success' };
    }
    if (hackathon.startDate && dayjs(hackathon.startDate).isAfter(now)) {
        return { phase: 'upcoming', label: 'Upcoming', color: 'info' };
    }
    return { phase: 'in-progress', label: 'In progress', color: 'primary' };
}

export const formatDate = (date: Date | null | undefined, format = 'MMM D, YYYY') => {
    return date ? dayjs(date).format(format) : '';
}

export const formatDateRange = (start: Date | null | undefined, end: Date | null | undefined) => {
    if (!start && !end) {
        return '';
    }
    if (start && end) {
        const s = dayjs(start);
        const e = dayjs(end);
        if (s.isSame(e, 'day')) {
            return s.format('MMM D, YYYY');
        }
        if (s.isSame(e, 'year')) {
            return `${s.format('MMM D')} – ${e.format(s.isSame(e, 'month') ? 'D, YYYY' : 'MMM D, YYYY')}`;
        }
        return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
    }
    return formatDate(start || end);
}

type TeamFields = Pick<HackathonPayload, 'votingPeriodOn' | 'endDate' | 'winners' | 'resultsAnnounced' | 'ideas' | 'teamsLocked'>;

/**
 * Why nobody can join or leave a team right now, or null when teams are open. Mirrors the server's rule:
 * teams stay open until voting starts, results are announced or the event is over.
 */
export const teamsLockedReason = (hackathon: Omit<TeamFields, 'ideas'>, now = new Date()): string | null => {
    // the organizers' own lock, whatever the phase
    if (hackathon.teamsLocked) {
        return 'Teams are locked by the organizers';
    }
    if (hackathon.votingPeriodOn) {
        return 'Teams are locked while voting is open';
    }
    if (resultsAnnounced(hackathon)) {
        return 'Teams are locked: results have been announced';
    }
    if (hackathon.endDate && dayjs(hackathon.endDate).endOf('day').isBefore(now)) {
        return 'Teams are locked: the hackathon has ended';
    }
    return null;
}

/** Why the user can't join a team right now, or null when they can. */
export const teamJoinBlockedReason = (hackathon: TeamFields, userEmail: string | undefined, now = new Date()): string | null => {
    if (!userEmail) {
        return 'Log in to join a team';
    }
    const locked = teamsLockedReason(hackathon, now);
    if (locked) {
        return locked;
    }
    // being on a team doesn't block joining another: Join asks before moving you (see currentTeamOf)
    return null;
}

/** The team the user is on, if any: joining another one first asks them to confirm leaving it. */
export const currentTeamOf = (hackathon: Pick<HackathonPayload, 'ideas'>, userEmail: string | undefined) =>
    userEmail ? hackathon.ideas.find(i => !i.archived && i.members.some(m => m.email === userEmail)) : undefined;

/** The last time anything happened: the hackathon changed (people joined, voting, results) or an idea was pitched or edited. */
export const lastActivity = (hackathon: Pick<HackathonPayload, 'updatedAt' | 'createdAt' | 'ideas'>): Date | null => {
    const times = [hackathon.updatedAt, hackathon.createdAt, ...hackathon.ideas.flatMap(i => [i.submittedAt, i.lastUpdatedAt])]
        .map(t => t ? new Date(t).getTime() : NaN)
        .filter(t => !isNaN(t));
    return times.length ? new Date(Math.max(...times)) : null;
}

/**
 * What the user does in this hackathon, said the way a card can show it: their role, their team, or the next step.
 */
export const myPart = (hackathon: Pick<HackathonPayload, 'organizers' | 'judges' | 'ideas' | 'votingPeriodOn' | 'winners' | 'ideaSubmissionDeadline'>, email: string | undefined): string | null => {
    if (!email) {
        return null;
    }
    const finished = hackathon.winners?.length > 0;
    if (hackathon.organizers.some(o => o.email === email)) {
        return hackathon.votingPeriodOn ? "You're organizing · voting is open" : "You're organizing";
    }
    if (hackathon.judges.some(j => j.email === email)) {
        return hackathon.votingPeriodOn ? "You're judging · score the teams" : "You're judging";
    }
    const team = hackathon.ideas.find(i => !i.archived && i.members.some(m => m.email === email));
    if (team) {
        return `Your team: ${team.title}`;
    }
    return !finished && ideasOpen(hackathon) ? 'Not on a team yet' : null;
}
